import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import type {
  MenuHeader,
  HolidayMenu,
  MenuHeaderCost,
  MenuHeaderItemCost,
} from '../types/menuCycle';
import type {
  MenuShareTargetFacility,
  MenuShareRequest,
  MenuShareResult,
  MenuShareLogEntry,
  MenuDeviationSummary,
} from '../types/menuShare';

// ===== QUERY KEYS =====

export const menuCycleKeys = {
  all: ['menuCycle'] as const,
  list: (facilityId: number) => [...menuCycleKeys.all, 'list', facilityId] as const,
  detail: (facilityId: number, id: number) =>
    [...menuCycleKeys.all, 'detail', facilityId, id] as const,
  deleted: (facilityId: number) =>
    [...menuCycleKeys.all, 'deleted', facilityId] as const,
  /** `useHolidayMenus`: HolidayMenu rows (the editor's Holiday menus list reads `mealtime`, `start_Date`). */
  holidayMenus: (facilityId: number, menuHeaderId: number) =>
    [...menuCycleKeys.all, 'holidayMenus', facilityId, menuHeaderId] as const,
  /** `useHolidayMenusForParent`: the Holiday/Override Menus window's grid rows (a different shape:
   *  `nextDate`, `isVisible`). It shared the key above, so whichever loaded first fed the other
   *  (the editor's list showed the window's rows and read every menu as "no date"). */
  holidayMenusForParent: (facilityId: number, parentMenuHeaderId: number) =>
    [...menuCycleKeys.all, 'holidayMenusForParent', facilityId, parentMenuHeaderId] as const,
  holidayMenusActive: (facilityId: number, menuHeaderId: number) =>
    [...menuCycleKeys.all, 'holidayMenusActive', facilityId, menuHeaderId] as const,
  holidayMenusDeleted: (facilityId: number, menuHeaderId: number) =>
    [...menuCycleKeys.all, 'holidayMenusDeleted', facilityId, menuHeaderId] as const,
  cost: (facilityId: number, menuHeaderId: number) =>
    [...menuCycleKeys.all, 'cost', facilityId, menuHeaderId] as const,
  itemCosts: (facilityId: number, menuHeaderId: number) =>
    [...menuCycleKeys.all, 'itemCosts', facilityId, menuHeaderId] as const,
};

// ===== MENU CYCLE LIST =====

export function useMenuCycles(facilityId: number) {
  return useQuery({
    queryKey: menuCycleKeys.list(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeader[]>(
        API.menuHeaders(facilityId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== MENU HEADER TREE (flat list: masters + holidays with parent FK) =====
// GET /api/MenuHeader/Tree/{facility} returns a flat array of
// `MenuHeaderTreeNode` (shape in types/mealPattern.ts) where
// `FKey_Menu_Hdr` is null for master menus and points at the parent
// PKey for holiday / override menus. `PKey_Menu_Hdr === 0` is a
// synthetic "(All Menus)" row the picker in `people/MenuPicker` uses
// but the Copy dialog does NOT want — consumers filter that row out
// themselves. Rows arrive `ORDER BY Menu_Name` from the DAL, so
// alphabetical ordering is already applied (both for master rows
// and their nested holidays).
export interface MenuHeaderTreeNodeApi {
  pKey_Menu_Hdr: number;
  fKey_Menu_Hdr: number | null;
  menu_Name: string;
  start_Date: string | null;
  end_Date: string | null;
  numberOfDays: number;
  mealtime: number | null;
  menuType: number | null;
  menuDay: number | null;
  isAllMenu: boolean;
  active: boolean;
}

export function useMenuHeaderTree(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuHeaderTree', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeaderTreeNodeApi[]>(
        API.menuHeaderTreeForFacility(facilityId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// ===== SINGLE MENU CYCLE =====

export function useMenuCycleById(facilityId: number, menuHeaderId: number) {
  return useQuery({
    queryKey: menuCycleKeys.detail(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeader>(
        API.menuHeaderById(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== DELETED MENU CYCLES =====

export function useDeletedMenuCycles(facilityId: number) {
  return useQuery({
    queryKey: menuCycleKeys.deleted(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeader[]>(
        API.menuHeaderDeleted(facilityId),
      );
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== HOLIDAY MENUS =====

export function useHolidayMenus(
  facilityId: number,
  menuHeaderId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: menuCycleKeys.holidayMenus(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenu[]>(
        API.menuHeaderHolidayMenus(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== ADD/MODIFY MENU CYCLE =====

export function useAddModMenuCycle(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeader: Partial<MenuHeader>) => {
      const { data } = await apiClient.post<number>(
        API.menuHeaderAddMod(facilityId),
        menuHeader,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== SOFT DELETE =====

export function useDeleteMenuCycle(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeaderId: number) => {
      await apiClient.delete(API.menuHeaderDelete(facilityId, menuHeaderId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== FULL WINFORMS DELETE CASCADE =====

/** Preflight counts used to build the WinForms confirm dialog text.
 *  Mirrors server DTO `DeletePreflight`. */
export interface MenuCycleDeletePreflight {
  pKey_Menu_Hdr: number;
  menuName: string;
  facilitiesInUse: number;
  employeeMenuFacilities: number;
  liveChildHolidays: number;
}

/** Result of the cascade delete. Mirrors server DTO
 *  `DeleteMenuHeaderResult`. */
export interface MenuCycleDeleteResult {
  pKey_Menu_Hdr: number;
  menuName: string;
  delete_Date: string;
  childHolidaysDeleted: number;
}

/** One-shot GET of the preflight counts. Not a query (no cache);
 *  fetched on demand right before showing the confirm dialog so the
 *  numbers can't be stale by the time the user clicks Yes. */
export function useDeletePreflightFetch(facilityId: number) {
  return useCallback(
    async (menuHeaderId: number): Promise<MenuCycleDeletePreflight> => {
      const { data } = await apiClient.get<MenuCycleDeletePreflight>(
        API.menuHeaderDeletePreflight(facilityId, menuHeaderId),
      );
      return data;
    },
    [facilityId],
  );
}

/** Full WinForms Delete cascade. Invalidates BOTH the active list
 *  and the deleted list so the Recycle Bin count updates too. */
export function useDeleteMenuCycleCascade(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeaderId: number): Promise<MenuCycleDeleteResult> => {
      const { data } = await apiClient.post<MenuCycleDeleteResult>(
        API.menuHeaderDeleteCascade(facilityId, menuHeaderId),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.deleted(facilityId) });
    },
  });
}

/** WinForms `DeleteHolidayMenuHeader` cascade — soft-delete a single
 *  detail (holiday/override) row: deactivates its mfh rows, stamps
 *  Delete_Date, writes audit log. Invalidates the parent's detail
 *  feed AND the holiday-count-by-parent map (used to hide empty
 *  master-detail chevrons). */
export function useDeleteHolidayCascade(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeaderId: number): Promise<MenuCycleDeleteResult> => {
      const { data } = await apiClient.post<MenuCycleDeleteResult>(
        API.menuHeaderDeleteHolidayCascade(facilityId, menuHeaderId),
      );
      return data;
    },
    onSuccess: async () => {
      // Blanket invalidate — the parent might have been the last
      // live child, in which case both the master-detail feed AND
      // the holiday-count map (drives chevron visibility) need to
      // refetch. AWAIT so callers can trust that the grid list is
      // fresh by the time the mutation resolves (needed for the
      // Holiday/Override dialog's post-delete focus math).
      await queryClient.invalidateQueries({ queryKey: ['menuCycle'] });
      // Recycle bin keys don't overlap `['menuCycle']` — invalidate
      // the bin explicitly so a soft-delete shows up if the bin
      // page is already mounted (or is mounted before the
      // recycle-bin staleTime elapses).
      queryClient.invalidateQueries({ queryKey: ['menuCycleRecycleBin'] });
    },
  });
}

// ===== RESTORE =====

export function useRestoreMenuCycle(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeaderId: number) => {
      await apiClient.post(API.menuHeaderRestore(facilityId, menuHeaderId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.deleted(facilityId) });
    },
  });
}

// ===== SET ACTIVE STATUS =====

/** Returns the PKey of the previously active menu (or null) */
export function useSetActiveStatus(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      menuHeaderId,
      isActive,
    }: {
      menuHeaderId: number;
      isActive: boolean;
    }) => {
      const { data } = await apiClient.post<number | null>(
        API.menuFacilitySetActive(facilityId, menuHeaderId, isActive),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== ADD/MODIFY DIALOG — General Information tab =====

/** Snapshot of the tblMenu_Header General Information + Rules fields
 *  plus per-facility notes memo for a single menu cycle. Used by the
 *  Add/Modify dialog's Modify path. */
export interface MenuCycleMetadata {
  pKey_Menu_Hdr: number;
  menu_Name: string;
  numberOfDays: number;
  start_Date: string | null;
  notes: string;
  // Rules tab (AM-3)
  doNotProceedToAS: boolean;
  doNotUseMPItems: boolean;
  employee_B: boolean;
  employee_L: boolean;
  employee_D: boolean;
}

export interface SaveMenuCycleMetadataRequest {
  /** null / 0 = Add (server assigns PKey); > 0 = Modify */
  pKey_Menu_Hdr: number | null;
  menu_Name: string;
  numberOfDays: number;
  start_Date: string;
  notes: string;
  doNotProceedToAS: boolean;
  doNotUseMPItems: boolean;
  employee_B: boolean;
  employee_L: boolean;
  employee_D: boolean;
}

export interface SaveMenuCycleMetadataResult {
  pKey_Menu_Hdr: number;
  menu_Name: string;
  isNew: boolean;
}

export function useMenuCycleMetadata(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'metadata', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<MenuCycleMetadata>(
        API.menuHeaderMetadata(facilityId, menuHeaderId),
      );
      return data;
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    // Metadata is user-edited via a dialog; refetch on window focus
    // is undesirable while the user is mid-edit. Stale after 1min.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveMenuCycleMetadata(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: SaveMenuCycleMetadataRequest): Promise<SaveMenuCycleMetadataResult> => {
      const { data } = await apiClient.post<SaveMenuCycleMetadataResult>(
        API.menuHeaderSaveMetadata(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      // Invalidate everything menu-cycle so both the master list AND
      // the metadata query for this menu refetch. Broad on purpose —
      // the AM-2 save writes to tblMenu_Header + tblMenu_Facility_Notes;
      // downstream slices will write more tables, and we don't want to
      // maintain a fragile per-key invalidation list.
      queryClient.invalidateQueries({ queryKey: ['menuCycle'] });
      queryClient.invalidateQueries({ queryKey: ['menuCycle', 'metadata', facilityId, res.pKey_Menu_Hdr] });
    },
  });
}

// ===== HOLIDAY MENUS FOR MASTER-DETAIL (per-facility IsActive) =====

/** Row shape returned by the master-detail feed. Includes per-
 *  facility IsActive from the tblMenu_Facility join, which the plain
 *  HolidayMenu type from `useHolidayMenus` doesn't populate. */
export interface HolidayForMasterDetail {
  pKey_Menu_Hdr: number;
  fKey_Menu_Hdr: number;
  menu_Name: string;
  menuType: number;               // 0 = Holiday, 1 = Always Served
  menuDay: number | null;         // null = one-time; int = recurring cycle day
  mealtime: number;               // cycle mealtime (0=Morning / 1=Afternoon / 2=Evening)
  start_Date: string | null;
  end_Date: string | null;
  note: string | null;
  isActive: boolean;              // for the CURRENT facility
}

export function useHolidayMenusForMasterDetail(
  facilityId: number,
  menuHeaderId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ['menuCycle', 'holidayMenusMD', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayForMasterDetail[]>(
        API.menuHeaderHolidayMenusMasterDetail(facilityId, menuHeaderId),
      );
      return data;
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    staleTime: 30 * 1000,
  });
}

// ===== DIET TYPES / CONSISTENCIES TAB (AM-7) =====

/** Master lookup row for the Diet Type picker (facility-scoped —
 *  includes any per-facility SortIndex / color overrides). Trimmed
 *  from the full TblDiet_Type C# DTO to just the columns the tab
 *  actually reads. */
export interface DietTypeOption {
  pKey_Diet_Type: number;
  name: string;
  /** ARGB signed int (`Color.ToArgb()`). Used by the Excel Sort
   *  sub-dialog to color the closed-state dropdown trigger with
   *  the picked diet type's palette (matches WinForms
   *  GLuDietTypeConsist_InMC.lu_EditValueChanged .vb:3499-3502). */
  argb_Back: number;
  argb_Fore: number;
}

export interface ConsistOption {
  pKey_Consist: number;
  name: string;
  argb_Back: number;
  argb_Fore: number;
}

/** Row shape shared by both side-by-side grids. One row is either
 *  a diet type OR a consist (server enforces mutual exclusion of
 *  fKey_Diet_Type / fKey_Consist). PKey_Prd_Menu_DF <= 0 signals a
 *  new row to the save endpoint. */
export interface PrdMenuDFRow {
  pKey_Prd_Menu_DF: number;
  fKey_Menu_Header: number;
  fKey_Diet_Type: number | null;
  fKey_Consist: number | null;
  priority: number;
  priority_Extension: number;
  /** DB stores as int (0/1). UI treats as boolean. */
  defaultValue: number;
}

export function useDietTypeMasterList(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'dietTypeMasterList', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<DietTypeOption[]>(
        API.prdMenuDietTypeMasterList(facilityId),
      );
      return data;
    },
    enabled: enabled && facilityId > 0,
    // Master data — rarely changes; long stale + no refetch on focus.
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useConsistMasterList(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'consistMasterList', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<ConsistOption[]>(
        API.prdMenuConsistMasterList(facilityId),
      );
      return data;
    },
    enabled: enabled && facilityId > 0,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useMenuDFRows(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'dfRows', facilityId, menuHeaderId],
    queryFn: async () => {
      // Reuse the existing GET endpoint — /api/PrdMenu/DietFilter/
      // — which already returns TblPrd_Menu_DF (now with
      // DefaultValue after the DTO extension).
      const { data } = await apiClient.get<PrdMenuDFRow[]>(
        API.prdMenuDietFilter(facilityId, menuHeaderId),
      );
      // Re-sort by PKey_Prd_Menu_DF ASC to match WinForms
      // DataTable-insertion order. The legacy `GetMenuDietFilter`
      // DAL has `ORDER BY Priority` in its inline SQL — we don't
      // touch legacy code, but WinForms `SQL_Prd_Menu_DF_Select`
      // has no ORDER BY, so its DataView iterates in PKey
      // (clustered-index) order. The Extension Sheet Sort
      // auto-populate step walks that order, so the client array
      // must match or the auto-fill sequence differs from
      // WinForms.
      return (data ?? []).slice().sort((a, b) => a.pKey_Prd_Menu_DF - b.pKey_Prd_Menu_DF);
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    // User-edited via the dialog — refetch on focus would clobber
    // pending edits. Manual invalidation via the save mutation.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveMenuDietTypeConsist(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuHeaderId, rows }: { menuHeaderId: number; rows: PrdMenuDFRow[] }): Promise<number> => {
      const { data } = await apiClient.post<number>(
        API.prdMenuSaveDietTypeConsist(facilityId, menuHeaderId),
        rows,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      // Refetch the diet-filter list for this menu so the dialog
      // reopens with server-truth PKeys on new rows.
      queryClient.invalidateQueries({ queryKey: ['menuCycle', 'dfRows', facilityId, vars.menuHeaderId] });
    },
  });
}

// ===== AM-9: Extension Sheet Excel Sort =====
// One row per "Excel diet order" — inline 5×DT + 5×Consist slots
// with a foreground + background color pair. Priority 1..N per
// menu.
export interface PrdMenuExtensionExcelRow {
  pKey_Prd_Menu_Extension_DF: number;
  fKey_Menu_Header: number;
  priority: number;
  argb_Back: number;
  argb_Fore: number;
  fKey_Diet_Type_1: number | null;
  fKey_Diet_Type_2: number | null;
  fKey_Diet_Type_3: number | null;
  fKey_Diet_Type_4: number | null;
  fKey_Diet_Type_5: number | null;
  fKey_Consist_1: number | null;
  fKey_Consist_2: number | null;
  fKey_Consist_3: number | null;
  fKey_Consist_4: number | null;
  fKey_Consist_5: number | null;
}

export function useMenuExtensionExcelRows(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'extensionExcel', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<PrdMenuExtensionExcelRow[]>(
        API.prdMenuExtensionExcel(facilityId, menuHeaderId),
      );
      return (data ?? []).slice().sort((a, b) => a.priority - b.priority);
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveMenuExtensionExcel(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuHeaderId, rows }: {
      menuHeaderId: number; rows: PrdMenuExtensionExcelRow[];
    }): Promise<number> => {
      const { data } = await apiClient.post<number>(
        API.prdMenuSaveExtensionExcel(facilityId, menuHeaderId),
        rows,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'extensionExcel', facilityId, vars.menuHeaderId],
      });
    },
  });
}

// ===== AM-4: Facility Permissions =====
// Zero rows = "Shared by all Facilities" (unrestricted). ≥1 rows
// restricts the menu to just those facilities.
export interface MenuFacilityPermissionRow {
  pKey_Menu_Header_FacilityPermissions: number;
  fKey_Menu_Hdr: number;
  fKey_Facility: number;
}

export function useMenuFacilityPermissions(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'facilityPermissions', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<MenuFacilityPermissionRow[]>(
        API.prdMenuFacilityPermissions(facilityId, menuHeaderId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Server response shape from `POST /SaveFacilityPermissions`.
 *  `autoAddedFacilityNames` is populated (usually empty) when the
 *  server auto-inserted permission rows for facilities that have
 *  this menu as their ACTIVE menu but weren't in the client's
 *  payload — ports WinForms `CheckFacilityMenuSecurityInUse`
 *  (frmDietech_Menu_Cycle_AddMod.vb:2664-2701). The client shows
 *  an info toast citing the names when the list is non-empty. */
export interface SaveMenuFacilityPermissionsResult {
  savedCount: number;
  autoAddedFacilityNames: string[];
}

export function useSaveMenuFacilityPermissions(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuHeaderId, rows }: {
      menuHeaderId: number; rows: MenuFacilityPermissionRow[];
    }): Promise<SaveMenuFacilityPermissionsResult> => {
      const { data } = await apiClient.post<SaveMenuFacilityPermissionsResult>(
        API.prdMenuSaveFacilityPermissions(facilityId, menuHeaderId),
        rows,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'facilityPermissions', facilityId, vars.menuHeaderId],
      });
    },
  });
}

// ===== AM-5: Dining Rooms =====
// Per-mealtime dining-room assignment for this menu at the current
// facility. `Mealtime`: 0=Breakfast, 1=Lunch, 2=Dinner.
export interface LayoutDiningRoomOption {
  pKey_LayoutDiningRoom: number;
  fKey_Facility: number | null;
  diningRoomName: string;
  description: string | null;
}
export interface MenuDiningRoomAssignmentRow {
  pKey_Menu_Header_Facility_DiningRoom: number;
  fKey_Facility: number;
  fKey_LayoutDiningRoom: number;
  mealtime: number;
  fKey_Menu_Hdr: number;
}

export function useFacilityDiningRooms(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'facilityDiningRooms', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<LayoutDiningRoomOption[]>(
        API.prdMenuFacilityDiningRooms(facilityId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useMenuDiningRoomAssignments(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'diningRoomAssignments', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<MenuDiningRoomAssignmentRow[]>(
        API.prdMenuDiningRoomAssignments(facilityId, menuHeaderId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveMenuDiningRoomAssignments(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuHeaderId, rows }: {
      menuHeaderId: number; rows: MenuDiningRoomAssignmentRow[];
    }): Promise<number> => {
      const { data } = await apiClient.post<number>(
        API.prdMenuSaveDiningRoomAssignments(facilityId, menuHeaderId),
        rows,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'diningRoomAssignments', facilityId, vars.menuHeaderId],
      });
    },
  });
}

// ===== AM-6: Selective Touch Printers =====
// Zero rows for a (menu, facility) = "All Facility Printers Active".
// ≥1 rows restrict the menu to just the listed printers.
// PrinterType is a small enum mirroring WinForms DT_PrinterTypes
// (Diet_Utils.vb:7864): 0=Menu Ticket, 1=Always Served,
// 2=Receipt Ticket, 3=Printer Categories, 4=Scheduled Reports.
export interface FacilityPrinterOption {
  pKey_Facility_Printer: number;
  fKey_Facility: number;
  friendlyName: string;
  printerName: string;
  printerType: number;
  enabled: boolean;
}
export interface MenuPrinterAssignmentRow {
  pKey_Menu_Facility_Printer: number;
  fKey_Facility: number;
  fKey_Menu_Hdr: number;
  fKey_Facility_Printer: number;
}

export function useFacilityPrinters(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'facilityPrinters', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<FacilityPrinterOption[]>(
        API.prdMenuFacilityPrinters(facilityId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useMenuPrinterAssignments(facilityId: number, menuHeaderId: number, enabled = true) {
  return useQuery({
    queryKey: ['menuCycle', 'printerAssignments', facilityId, menuHeaderId],
    queryFn: async () => {
      const { data } = await apiClient.get<MenuPrinterAssignmentRow[]>(
        API.prdMenuPrinterAssignments(facilityId, menuHeaderId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveMenuPrinterAssignments(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuHeaderId, rows }: {
      menuHeaderId: number; rows: MenuPrinterAssignmentRow[];
    }): Promise<number> => {
      const { data } = await apiClient.post<number>(
        API.prdMenuSavePrinterAssignments(facilityId, menuHeaderId),
        rows,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'printerAssignments', facilityId, vars.menuHeaderId],
      });
    },
  });
}

/** Local mirror of WinForms `DT_PrinterTypes` (Diet_Utils.vb:7864).
 *  Static enum — 5 types plus the facility-setting-gated
 *  "Printer Categories" (id=3). Deferred: gate id=3 on
 *  `Printers_EnablePrinterCategories` (facility setting). */
export const PRINTER_TYPE_LABELS: Record<number, string> = {
  0: 'Menu Ticket',
  1: 'Always Served',
  2: 'Receipt Ticket',
  3: 'Printer Categories',
  4: 'Scheduled Reports',
};

// ===== HOLIDAY COUNTS BY PARENT =====

/** `{ [parentPKey]: count }` — parents omitted have no live
 *  children. Used to hide the master-detail chevron on empty rows. */
export function useHolidayCountsByParent(facilityId: number) {
  return useQuery({
    queryKey: ['menuCycle', 'holidayCounts', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<Record<string, number>>(
        API.menuHeaderHolidayCountsByParent(facilityId),
      );
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

// ===== ACTIVATE (WinForms-parity, activate-only + audit log) =====

/** Result of the WinForms-parity Activate call. Mirrors server DTO
 *  `ActivateResult`. `wasAlreadyActive` lets the client skip its
 *  success toast for the no-op case. */
export interface MenuCycleActivateResult {
  pKey_Menu_Hdr: number;
  menuName: string;
  fKey_Facility: number;
  previousMenuPKey: number | null;
  previousMenuName: string;
  wasAlreadyActive: boolean;
}

/** WinForms-parity activate-only mutation. Server refuses to
 *  deactivate (silent no-op if the menu is already active), writes
 *  audit-log Modify rows for the newly-activated menu AND the
 *  previously-active one. */
export function useActivateMenuCycle(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menuHeaderId: number): Promise<MenuCycleActivateResult> => {
      const { data } = await apiClient.post<MenuCycleActivateResult>(
        API.menuFacilityActivate(facilityId, menuHeaderId),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== ADD/MODIFY HOLIDAY MENU =====

export function useAddModHolidayMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (holidayMenu: Partial<HolidayMenu>) => {
      const { data } = await apiClient.post<number>(
        API.menuHeaderAddMod(facilityId),
        holidayMenu,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables.fKey_Menu_Hdr) {
        queryClient.invalidateQueries({
          queryKey: menuCycleKeys.holidayMenus(facilityId, variables.fKey_Menu_Hdr),
        });
        queryClient.invalidateQueries({
          queryKey: menuCycleKeys.holidayMenusForParent(facilityId, variables.fKey_Menu_Hdr),
        });
      }
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== DELETE HOLIDAY MENU =====

export function useDeleteHolidayMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      menuHeaderId,
      parentMenuHeaderId,
    }: {
      menuHeaderId: number;
      parentMenuHeaderId: number;
    }) => {
      await apiClient.delete(API.menuHeaderDelete(facilityId, menuHeaderId));
      return parentMenuHeaderId;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenus(facilityId, variables.parentMenuHeaderId),
      });
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenusForParent(facilityId, variables.parentMenuHeaderId),
      });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== ACTIVE HOLIDAY MENUS =====

export function useActiveHolidayMenus(
  facilityId: number,
  menuHeaderId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: menuCycleKeys.holidayMenusActive(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenu[]>(
        API.holidayMenusActive(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== DELETED HOLIDAY MENUS =====

export function useDeletedHolidayMenus(
  facilityId: number,
  menuHeaderId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: menuCycleKeys.holidayMenusDeleted(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenu[]>(
        API.holidayMenuDeleted(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== RESTORE HOLIDAY MENU =====

export function useRestoreHolidayMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      menuHeaderId,
      parentMenuHeaderId,
    }: {
      menuHeaderId: number;
      parentMenuHeaderId: number;
    }) => {
      await apiClient.post(API.holidayMenuRestore(facilityId, menuHeaderId));
      return parentMenuHeaderId;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenusDeleted(facilityId, variables.parentMenuHeaderId),
      });
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenus(facilityId, variables.parentMenuHeaderId),
      });
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenusForParent(facilityId, variables.parentMenuHeaderId),
      });
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.holidayMenusActive(facilityId, variables.parentMenuHeaderId),
      });
    },
  });
}

// ===== HOLIDAY MENU BY ID =====

export function useHolidayMenuById(
  facilityId: number,
  id: number,
  enabled = true,
) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'holidayMenuDetail', facilityId, id],
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenu>(
        API.holidayMenuById(facilityId, id),
      );
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: enabled && facilityId > 0 && id > 0,
  });
}

// ===== MENU HEADER COST =====

export function useMenuHeaderCost(facilityId: number, menuHeaderId: number) {
  return useQuery({
    queryKey: menuCycleKeys.cost(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeaderCost>(
        API.menuHeaderCost(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== MENU HEADER ITEM COSTS =====

export function useMenuHeaderItemCosts(facilityId: number, menuHeaderId: number) {
  return useQuery({
    queryKey: menuCycleKeys.itemCosts(facilityId, menuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuHeaderItemCost[]>(
        API.menuHeaderItemCost(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0 && menuHeaderId > 0,
  });
}

// Menu Cost saves through useSaveMenuCost (menuCycleToolsApi.ts).

// ===== ALWAYS SERVED ITEMS =====

export function useMenuHeaderAlwaysServed(facilityId: number) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'alwaysServed', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get(API.menuHeaderAlwaysServed(facilityId));
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== MENU FACILITY CURRENT STATUS =====

export interface MenuFacilityCurrent {
  fKey_Menu_Header: number;
  isActive: boolean;
  currentDay: number;
  currentMealtime: number;
  lastActivatedDate: string | null;
}

export function useMenuFacilityCurrent(facilityId: number, menuHeaderId: number) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'facilityCurrent', facilityId, menuHeaderId] as const,
    queryFn: async (): Promise<MenuFacilityCurrent | null> => {
      const { data } = await apiClient.get<MenuFacilityCurrent>(
        API.menuFacilityCurrent(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 60 * 1000,
    enabled: facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== MENU SHARING — RELATED FACILITIES =====

export function useRelatedFacilities(facilityId: number, enabled = false) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'relatedFacilities', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MenuShareTargetFacility[]>(
        API.menuHeaderRelatedFacilities(facilityId),
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: enabled && facilityId > 0,
  });
}

// ===== MENU SHARING — SHARE MENU =====

export function useShareMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: MenuShareRequest) => {
      const { data } = await apiClient.post<MenuShareResult[]>(
        API.menuHeaderShare(facilityId),
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
    },
  });
}

// ===== MENU SHARING — SHARE STATUS =====

export function useMenuShareStatus(
  facilityId: number,
  menuHeaderId: number,
  enabled = false,
) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'shareStatus', facilityId, menuHeaderId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MenuShareLogEntry[]>(
        API.menuHeaderShareStatus(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== MENU DEVIATION SUMMARY =====

export function useMenuDeviationSummary(
  facilityId: number,
  menuHeaderId: number,
  enabled = false,
) {
  return useQuery({
    queryKey: [...menuCycleKeys.all, 'deviationSummary', facilityId, menuHeaderId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MenuDeviationSummary>(
        API.menuHeaderDeviationSummary(facilityId, menuHeaderId),
      );
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: enabled && facilityId > 0 && menuHeaderId > 0,
  });
}

// ===== HD-1: Holiday/Override Menu grid (read-only) =====
// One row per holiday menu tied to a parent cycle. Server has
// already applied the facility-permission filter and computed
// `nextDate` via WinForms `GetNextHolidayMenuRecurrence`.
export interface HolidayMenuGridRow {
  pKey_Menu_Hdr: number;
  fKey_Menu_Hdr: number;   // parent cycle PKey
  menu_Name: string;
  mealtime: number;
  note: string | null;
  start_Date: string;
  menuDay: number | null;
  end_Date: string | null;
  menuType: number;        // 0 = Standard/Holiday, 1 = Always Served
  delete_Date: string | null;
  doNotUseMPItems: boolean;
  /** Effective "Date" column value the grid displays (WinForms
   *  unbound colStart_Date). Null when Always Served + no MenuDay. */
  nextDate: string | null;
  isVisible: boolean;
}

export function useHolidayMenusForParent(
  facilityId: number,
  parentMenuHeaderId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: menuCycleKeys.holidayMenusForParent(facilityId, parentMenuHeaderId),
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenuGridRow[]>(
        API.prdMenuHolidayMenusForParent(facilityId, parentMenuHeaderId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && parentMenuHeaderId > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ═════════════════════════════════════════════════════════════════
// HD-3: Holiday/Override Menu AddMod dialog
// ═════════════════════════════════════════════════════════════════

/** Server payload for the "Preserve Selective Menu Choices" radios. */
export interface HolidayMenuSMInfo {
  residentsOnSelectiveMenu: boolean;
  activeHolidayPKey_Menu_Hdr: number;   // 0 when none
  activeHolidayName: string;
}

/** Row loaded for edit (Modify path only — New uses local defaults). */
export interface HolidayMenuMetadata {
  pKey_Menu_Hdr: number;
  fKey_Menu_Hdr: number;
  menu_Name: string;
  menuType: number;                     // 0 = Standard/Holiday, 1 = Always Served
  mealtime: number;                     // 0/1/2 = Morning/Afternoon/Evening
  start_Date: string | null;
  end_Date: string | null;
  menuDay: number | null;
  note: string | null;
  doNotUseMPItems: boolean;
  permittedFacilityIds: number[];       // empty ⇒ shared with all
  /** Server-computed next real-world occurrence for MenuType=0 —
   *  WinForms `FillForm` uses this in the Date field on Modify. Null
   *  for MenuType=1; client falls back to `start_Date` there. */
  nextDate: string | null;
}

export type HolidayMenuPreserveSMChoice = 'None' | 'Main' | 'Holiday';

export interface HolidayMenuSaveRequest {
  /** 0 for New, > 0 for Modify. */
  pKey_Menu_Hdr: number;
  /** Parent cycle PKey — required. */
  fKey_Menu_Hdr: number;
  menu_Name: string;
  menuType: number;
  mealtime: number;
  start_Date: string | null;
  end_Date: string | null;
  menuDay: number | null;
  note: string | null;
  doNotUseMPItems: boolean;
  permittedFacilityIds: number[];
  /** New only. false ⇒ copy from Main cycle; true ⇒ copy from active recurring holiday / AS override. */
  useRecurringSource: boolean;
  /** New only. 'None' (default), 'Main', or 'Holiday'. */
  preserveSMChoices: HolidayMenuPreserveSMChoice;
  /** Set when preserveSMChoices === 'Holiday' — from SMInfo lookup. */
  preserveSourcePKey_Menu_Hdr: number | null;
}

export interface HolidayMenuSaveResult {
  pKey_Menu_Hdr: number;
  menu_Name: string;
  isNew: boolean;
  copiedMenuDetailCount: number;
  copiedSelectiveMenuCount: number;
  /** New only: the items were copied and the holiday was switched on.
   *  False when the server left it off because the source had no items for
   *  that day and mealtime (an empty holiday would serve a blank menu for
   *  the whole meal). Older API builds omit it. */
  activated?: boolean;
  /** Message to show the operator: created and turned on (what was copied),
   *  or why it was not turned on. */
  notice?: string | null;
}

export function useHolidayMenuMetadata(
  facilityId: number,
  pKey_Menu_Hdr: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ['menuCycle', 'holidayMenuMetadata', facilityId, pKey_Menu_Hdr],
    queryFn: async () => {
      const { data } = await apiClient.get<HolidayMenuMetadata>(
        API.prdMenuHolidayMenuMetadata(facilityId, pKey_Menu_Hdr),
      );
      return data;
    },
    enabled: enabled && facilityId > 0 && pKey_Menu_Hdr > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useHolidayMenuSMInfo(
  facilityId: number,
  parentPKey_Menu_Hdr: number,
  mealtime: number,
  forDate: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['menuCycle', 'holidayMenuSMInfo', facilityId, parentPKey_Menu_Hdr, mealtime, forDate],
    queryFn: async () => {
      const q = forDate ? `?forDate=${encodeURIComponent(forDate)}` : '';
      const { data } = await apiClient.get<HolidayMenuSMInfo>(
        `${API.prdMenuHolidayMenuSMInfo(facilityId, parentPKey_Menu_Hdr, mealtime)}${q}`,
      );
      return data;
    },
    enabled: enabled && facilityId > 0 && parentPKey_Menu_Hdr > 0,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: false,
    // Keep the last-good SMInfo visible while a new (date/mealtime-
    // driven) refetch is in flight. Without this the query key change
    // clears `data` to undefined between requests, which makes the
    // Preserve SM Choices section flash off/on with every Date pick.
    placeholderData: (prev) => prev,
  });
}

// ─────────────────────────────────────────────────────────────
// Copy toolbar — Phase 1 (Mode B: Copy to EXISTING slice).
// Server-side port of WinForms
// `frmDietech_Menu_Cycle_CopyNew.btnCopy_Click` at .vb:2643-2733.
// Wraps the shared VB `ProductionMenuCycleCopyManager` under a
// pessimistic lock on the destination menu; response distinguishes
// "another user is holding the lock" (Acquired=false) from
// "copy actually failed" (500 error).
// ─────────────────────────────────────────────────────────────
export interface MenuCycleCopyToExistingRequest {
  fromPKey: number;
  toPKey: number;
  dayFrom: number;
  dayTo: number;
  breakfastFrom: boolean;
  lunchFrom: boolean;
  dinnerFrom: boolean;
  breakfastTo: boolean;
  lunchTo: boolean;
  dinnerTo: boolean;
  /** True when the picked From row is a holiday menu
   *  (`FKey_Menu_Hdr != null`). Server rewrites `dayFrom` to the
   *  sentinel `0` and skips the destination-slice DELETE. */
  isFromHoliday: boolean;
  /** True when the picked To row is a holiday menu. Server rewrites
   *  `dayTo` to the sentinel `0` and sets the manager's
   *  `IsCopyToHolidayMenu` flag. */
  isToHoliday: boolean;
}

export interface MenuCycleCopyResult {
  fromPKey: number;
  toPKey: number;
  acquired: boolean;
  success: boolean;
  lockedByUserId: number | null;
  lockedByUserName: string | null;
}

// ─────────────────────────────────────────────────────────────
// Copy Override — Copy From a holiday menu into a destination
// master, creating a NEW holiday menu underneath it. Port of the
// WinForms `panelCopyOverride` branch in `btnCopy_Click` at
// frmDietech_Menu_Cycle_CopyNew.vb:2554-2586. Server flow:
// CreateHolidayMenu (new tblMenu_Header) → CopyHolidayMenu (details
// via ProductionMenuCycleCopyManager.CopyHolidayMenu) →
// DeactivateMatchingOverrideMenus (flip matching Active=1 rows off).
// ─────────────────────────────────────────────────────────────
export type HolidayActivateForMode = 0 | 1 | 2;   // Facility | ExistingActivations | All
export const HOLIDAY_ACTIVATE_FOR = {
  Facility: 0 as const,
  ExistingActivations: 1 as const,
  All: 2 as const,
};

export interface MenuCycleCopyHolidayToOverrideRequest {
  fromPKey: number;
  toPKey: number;
  newName: string;
  startDate: string;   // ISO yyyy-mm-dd
  menuDay: number | null;
  mealtime: number;
  activateFor: HolidayActivateForMode;
  fKey_Facility_ActivateFor: number | null;
}

export interface MenuCycleCopyHolidayToOverrideResult {
  fromPKey: number;
  toPKey: number;
  newHolidayPKey: number;
  acquired: boolean;
  success: boolean;
  lockedByUserId: number | null;
  lockedByUserName: string | null;
}

// ─────────────────────────────────────────────────────────────
// Copy holiday menus to another menu cycle (Menu Cycles list and
// the Holiday/Override Menus window). Each source holiday becomes
// a new holiday on the target cycle with the same fields and items;
// its diet settings land on the target's diet columns of the same
// diet type / consistency, and the ones the target has no column
// for are dropped and reported. One transaction on the server
// (PrdMenuDAL.CopyHolidaysToCycle).
// ─────────────────────────────────────────────────────────────
export interface HolidayCopyToCycleRequest {
  sourcePKeys: number[];
  targetFKey_Menu_Hdr: number;
  /** Turn each copy on for this facility (when it has items), as a new holiday is. */
  turnOn: boolean;
}

export interface HolidayCopyDroppedDiet {
  name: string;
  isConsistency: boolean;
  /** Diet columns, combination overrides and custom path diets that named it. */
  settings: number;
}

export interface HolidayCopyToCycleItem {
  sourcePKey_Menu_Hdr: number;
  /** The new holiday menu. */
  pKey_Menu_Hdr: number;
  menu_Name: string;
  menuType: number;
  mealtime: number;
  start_Date: string;
  menuDay: number | null;
  rowsCopied: number;
  turnedOn: boolean;
  /** Holidays on the target the copy turned off for this facility (same slot). */
  turnedOff: string[];
  droppedDiets: HolidayCopyDroppedDiet[];
  notice: string | null;
}

export interface HolidayCopyToCycleResult {
  targetFKey_Menu_Hdr: number;
  targetMenuName: string;
  copies: HolidayCopyToCycleItem[];
}

export function useCopyHolidaysToCycle(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: HolidayCopyToCycleRequest): Promise<HolidayCopyToCycleResult> => {
      const { data } = await apiClient.post<HolidayCopyToCycleResult>(
        API.menuCycleModifyCopyHolidaysToCycle(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      // The Menu Cycles list (its holiday grid, the counts that show a cycle's
      // expand arrow) and every holiday list of the target cycle.
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: ['menuHeaderTree', facilityId] });
      queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayMenusMD', facilityId] });
      queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayCounts', facilityId] });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.holidayMenus(facilityId, res.targetFKey_Menu_Hdr) });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.holidayMenusForParent(facilityId, res.targetFKey_Menu_Hdr) });
      queryClient.invalidateQueries({ queryKey: menuCycleKeys.holidayMenusActive(facilityId, res.targetFKey_Menu_Hdr) });
    },
  });
}

export function useCopyHolidayToOverride(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: MenuCycleCopyHolidayToOverrideRequest): Promise<MenuCycleCopyHolidayToOverrideResult> => {
      const { data } = await apiClient.post<MenuCycleCopyHolidayToOverrideResult>(
        API.menuHeaderCopyHolidayToOverride(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
        queryClient.invalidateQueries({ queryKey: ['menuHeaderTree', facilityId] });
        queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayMenusMD', facilityId] });
        // holidayCounts gates the destination master's expand
        // chevron + `handleRowExpanding` — must refresh so the
        // freshly-created holiday makes the parent expandable.
        queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayCounts', facilityId] });
      }
    },
  });
}

/** Selective Menu bring-forward mode when creating a NEW menu
 *  cycle (WinForms radSMAll / radSMNone / radSMFacility). */
export const SELECTIVE_MENU_MODE = {
  None: 0 as const,
  All: 1 as const,
  Facility: 2 as const,
};
export type SelectiveMenuCopyMode = 0 | 1 | 2;

export interface MenuCycleCopyToNewRequest {
  fromPKey: number;
  newName: string;
  includeAS: boolean;
  includeOneTime: boolean;
  includeRecurring: boolean;
  activeMenusOnly: boolean;
  fKey_Facility_ActiveFor: number | null;
  selectiveMenuMode: SelectiveMenuCopyMode;
  /** Optional client-generated UUID (v4 recommended). When set,
   *  the server registers a `CopyProgressStore` entry and attaches
   *  a reporter to the copy manager; the client polls
   *  `useCopyProgress(id)` to render a determinate progress bar.
   *  Null → server runs without progress tracking (spinner-only). */
  operationId?: string | null;
}

export interface MenuCycleCopyToNewResult {
  fromPKey: number;
  newPKey: number;
  success: boolean;
}

/** Server-side snapshot of an in-flight Copy To New. Empty
 *  `title` + Total=0 means the DAL hasn't reached its first
 *  phase yet (freshly Register'd entry). */
export interface CopyProgressState {
  current: number;
  total: number;
  title: string;
  done: boolean;
  error: string | null;
}

/** Polls `/api/MenuHeader/CopyProgress/{operationId}` while
 *  `enabled` is true, at 400 ms cadence. Stops on Done or a
 *  404 (interpreted as "already retired, treat as done").
 *
 *  Usage: mint a UUID with `crypto.randomUUID()` before firing
 *  the Copy To New mutation, then enable this hook with the
 *  same UUID while the mutation is pending. */
export function useCopyProgress(operationId: string | null, enabled: boolean) {
  return useQuery<CopyProgressState | null>({
    queryKey: ['menuCycle', 'copyProgress', operationId],
    queryFn: async () => {
      if (!operationId) return null;
      try {
        const { data } = await apiClient.get<CopyProgressState>(
          API.menuHeaderCopyProgress(operationId),
        );
        return data;
      } catch (err: unknown) {
        // 404 = store TTL expired or entry never registered.
        // Both paths mean "no progress to show" — return a
        // terminal Done state so the client stops polling.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return { current: 0, total: 0, title: '', done: true, error: null };
        }
        throw err;
      }
    },
    enabled: enabled && !!operationId,
    // Plain 400 ms interval — React Query stops firing when
    // `enabled` flips to false (mutation resolves). We
    // intentionally do NOT return false when data.done, because
    // the callback signature can early-exit before data ever
    // arrives (undefined → false → no more polls, ever). Keeping
    // it constant means one extra poll after Finish() at worst;
    // the client's mutation resolves right after, disabling us.
    refetchInterval: 400,
    // Never cache stale — each poll should hit the network.
    staleTime: 0,
    gcTime: 0,
  });
}

/** Copy To New (Phase 2). Creates a new master menu cycle from a
 *  source master. WinForms `CreateNewMenuCycle` at
 *  frmDietech_Menu_Cycle_CopyNew.vb:3097-3446. */
export function useCopyToNewMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: MenuCycleCopyToNewRequest): Promise<MenuCycleCopyToNewResult> => {
      const { data } = await apiClient.post<MenuCycleCopyToNewResult>(
        API.menuHeaderCopyToNew(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
        queryClient.invalidateQueries({ queryKey: ['menuHeaderTree', facilityId] });
        queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayMenusMD', facilityId] });
        // A Copy To New that pulled holidays forward makes the new
        // master a parent-with-kids; the master grid gates its
        // chevron + row-expand on this cache. Also refresh for the
        // source master since its count didn't actually change but
        // costs nothing to refresh.
        queryClient.invalidateQueries({ queryKey: ['menuCycle', 'holidayCounts', facilityId] });
      }
    },
  });
}

export function useCopyMenuCycleToExisting(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: MenuCycleCopyToExistingRequest): Promise<MenuCycleCopyResult> => {
      const { data } = await apiClient.post<MenuCycleCopyResult>(
        API.menuHeaderCopyToExisting(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      // The destination slice's content changed. Invalidate the
      // menu list (for any "(Active)" affiliation drift) and the
      // Menu Cycle Modify caches for the destination menu so
      // reopening the modify dialog shows the copied rows.
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: menuCycleKeys.list(facilityId) });
        queryClient.invalidateQueries({
          queryKey: ['menuCycle', 'modifyLoad', facilityId, res.toPKey],
        });
      }
    },
  });
}

export function useSaveHolidayMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: HolidayMenuSaveRequest) => {
      const { data } = await apiClient.post<HolidayMenuSaveResult>(
        API.prdMenuSaveHolidayMenu(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (res) => {
      // Grid needs to refetch — new rows appear, existing ones may
      // have moved. Invalidate both holiday lists (the window's grid
      // and the editor's Holiday menus list) for every parent; a save
      // can move a holiday between parents.
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'holidayMenus'],
      });
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'holidayMenusForParent'],
      });
      // Metadata refetch so a subsequent re-open shows the just-saved
      // values (not the pre-save cache).
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'holidayMenuMetadata', facilityId, res.pKey_Menu_Hdr],
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Select/Unselect a holiday menu — toolbar Set Active click when
// a holiday-detail row is focused. Server-side WinForms port of
// `SelectHolidayMenu` at frmDietech_Menu_Cycle_DataGrid.vb:2644-2839.
// The server flips the row's Active flag and cascades to sibling
// holidays per MenuType (recurring / one-time / always-served).
// The response tells us the before/after Active state so we can
// update the client's focused-selection isActive marker without
// waiting for the invalidate refetch.
// ─────────────────────────────────────────────────────────────
export interface HolidayToggleResult {
  pKey_Menu_Hdr: number;
  fKey_Facility: number;
  menuName: string;
  previousActive: boolean;
  nowActive: boolean;
}

export function useSelectHolidayMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pKey_Menu_Hdr: number): Promise<HolidayToggleResult> => {
      const { data } = await apiClient.post<HolidayToggleResult>(
        API.prdMenuSelectHolidayMenu(facilityId, pKey_Menu_Hdr),
      );
      return data;
    },
    onSuccess: () => {
      // Sibling cascade may have flipped multiple rows — refetch the
      // whole master-detail holiday tree for the facility rather than
      // patch a single row's isActive.
      queryClient.invalidateQueries({
        queryKey: ['menuCycle', 'holidayMenusMD', facilityId],
      });
      // The parent Menu Cycle list caches per-row IsActive too (via
      // the master-menu MenuHeaderDAL.GetAll); invalidate it too so
      // any (Active) suffix stays truthful.
      queryClient.invalidateQueries({
        queryKey: menuCycleKeys.list(facilityId),
      });
    },
  });
}
